import {CheckInEventType} from '@lib/infrastructure';
import {createSelector} from '@reduxjs/toolkit';
import createCachedSelector from 're-reselect';
import {getIsCheckInFeatureEnabled, getIsManualCheckInFeatureEnabled} from '../../infrastructure/features/selectors';
import {RootState} from '../../rootReducer';
import {checkInStatusesAdapter} from './adapters';

export const getUserCheckInStatus = (state: RootState) => state.data.checkIn.status;

export const getIsUserCheckedIn = createSelector(
  getUserCheckInStatus,
  (status) => status && status.eventType === CheckInEventType.AtTheOffice,
);

export const getIsLoadingUserCheckIn = (state: RootState) => state.data.checkIn.isLoadingUserCheckIn;

export const getIsLoadingConnectionsCheckIn = (state: RootState) => state.data.checkIn.isLoadingConnectionsCheckIn;

export const {
  selectById: getConnectionsCheckInStatusById,
  selectIds: getConnectionsCheckInStatusIds,
  selectEntities: getConnectionsCheckInStatusEntities,
  selectAll: getAllConnectionsCheckInStatus,
  selectTotal: getTotalConnectionsCheckInStatuses,
} = checkInStatusesAdapter.getSelectors((state: RootState) => state.data.checkIn.connectionsCheckInStatus);

export const getAreAllCheckInFlagsEnabled = createSelector(
  getIsCheckInFeatureEnabled,
  getIsManualCheckInFeatureEnabled,
  (isCheckInFeatureEnabled, manualCheckInIsEnabled) => isCheckInFeatureEnabled && manualCheckInIsEnabled,
);

const isUserByIdOrDirectoryObjectId =
  (userOrDirectoryObjectId?: string) => (userInfo?: {id?: string; userId?: string; directoryObjectId?: string}) =>
    !!userInfo &&
    (userInfo.id === userOrDirectoryObjectId ||
      userInfo.userId === userOrDirectoryObjectId ||
      userInfo.directoryObjectId === userOrDirectoryObjectId);

export const getCheckInStatusByUserIdOrDirectoryObjectId = createCachedSelector(
  (state: RootState) => state.data.user.user,
  getUserCheckInStatus,
  getAllConnectionsCheckInStatus,
  getIsCheckInFeatureEnabled,
  (_state: RootState, userOrDirectoryObjectId: string | undefined) => userOrDirectoryObjectId,
  (user, userCheckInStatus, connectionsCheckInStatuses, isCheckInFeatureEnabled, userOrDirectoryObjectId) => {
    if (!isCheckInFeatureEnabled || !userOrDirectoryObjectId) return;

    if (isUserByIdOrDirectoryObjectId(userOrDirectoryObjectId)(user)) {
      return userCheckInStatus;
    }

    return connectionsCheckInStatuses.find(isUserByIdOrDirectoryObjectId(userOrDirectoryObjectId));
  },
)((_state, userOrDirectoryObjectId) => userOrDirectoryObjectId || '');

// TODO: Remove these two from here once migrated to the new calendar
export const getIsCheckInBottomSheetVisible = (state: RootState) => state.data.checkIn.isCheckInBottomSheetVisible;
